import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getFiscalConfig,
  saveFiscalConfig,
  uploadAfipCredentials,
  addPuntoVenta,
  updatePuntoVenta,
  getFiscalStatus,
} from '../features/fiscal/fiscalSlice';
import fiscalService from '../features/fiscal/fiscalService';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';

const FiscalConfig = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fiscalConfig, fiscalStatus, loading } = useSelector((state) => state.fiscal);

  const [activeTab, setActiveTab] = useState('datos-fiscales');
  const [showPuntoVentaModal, setShowPuntoVentaModal] = useState(false);
  const [editingPunto, setEditingPunto] = useState(false);
  const [editingOriginalNumero, setEditingOriginalNumero] = useState(null);
  const padronLoading = false; // no automatic padron lookups in this UI (setter unused)
  const [padronError, setPadronError] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  // padronRaw removed; we no longer show raw AFIP responses in the UI

  // Datos Fiscales
  const [datosForm, setDatosForm] = useState({
    cuit: '',
    razonSocial: '',
    nombreFantasia: '',
    ingresosBrutos: {
      numero: '',
      condicion: undefined
    },
    tipoResponsable: 'IVA Responsable Inscripto',
    condicionIVA: 1,
    inicioActividades: '',
    email: '',
    telefono: '',
    domicilioFiscal: {
      calle: '',
      numero: '',
      piso: '',
      departamento: '',
      localidad: '',
      provincia: '',
      codigoPostal: ''
    }
  });

  // Credenciales AFIP
  const [credencialesForm, setCredencialesForm] = useState({
    certificado: null,
    clavePrivada: null,
    passphrase: '',
    ambiente: 'testing'
  });

  // Punto de Venta
  const [puntoVentaForm, setPuntoVentaForm] = useState({
    numero: '',
    nombre: '',
    tipo: 'Electrónica',
    usage: 'sistema'
  });

  useEffect(() => {
    dispatch(getFiscalConfig());
    dispatch(getFiscalStatus());
  }, [dispatch]);


  useEffect(() => {
    if (fiscalConfig) {
      setDatosForm({
        cuit: fiscalConfig.cuit || '',
        razonSocial: fiscalConfig.razonSocial || '',
        nombreFantasia: fiscalConfig.nombreFantasia || '',
        ingresosBrutos: fiscalConfig.ingresosBrutos || { numero: '', condicion: undefined },
        tipoResponsable: fiscalConfig.tipoResponsable || 'IVA Responsable Inscripto',
        condicionIVA: fiscalConfig.condicionIVA || 1,
        inicioActividades: fiscalConfig.inicioActividades ? new Date(fiscalConfig.inicioActividades).toISOString().split('T')[0] : '',
        email: fiscalConfig.email || '',
        telefono: fiscalConfig.telefono || '',
        domicilioFiscal: fiscalConfig.domicilioFiscal || {
          calle: '',
          numero: '',
          piso: '',
          departamento: '',
          localidad: '',
          provincia: '',
          codigoPostal: ''
        }
      });
    }
  }, [fiscalConfig]);

  // Revalidar estado fiscal cuando la página reciba foco (útil para ver cambios hechos desde la Consola Admin sin recargar)
  useEffect(() => {
    const onFocus = () => {
      try { dispatch(getFiscalStatus()); dispatch(getFiscalConfig()); } catch (_) { }
    };
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('focus', onFocus); };
  }, [dispatch]);


  // Intentar re-bloquear al salir de la página (beforeunload) — colocado antes del early return para respetar el orden de Hooks
  useEffect(() => {
    const releaseAll = () => {
      try {
        const payloadObj = { all: true };
        const payload = JSON.stringify(payloadObj);

        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            if (user && user.token) {
              fetch('/api/fiscal/locks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                body: payload,
                keepalive: true
              }).catch(() => { });
              return;
            }
          }
        } catch (_) { /* parse error, caer al fallback */ }

        if (navigator && navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon('/api/fiscal/locks', blob);
          return;
        }

        fetch('/api/fiscal/locks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => { });
      } catch (_) { /* ignore */ }
    };

    const onBeforeUnload = () => {
      releaseAll();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    // Además de beforeunload (cerrar pestaña / refrescar / navegar fuera),
    // asegurar re-bloqueo cuando el componente se desmonta (navegación SPA)
    return () => {
      try { releaseAll(); } catch (_) { }
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const handleDatosChange = (e) => {
    const { name, value } = e.target;
    // Si cambia el tipo de responsable, sincronizar condicionIVA numérica
    if (name === 'tipoResponsable') {
      const tipo = value;
      // Mapear tipos legibles a códigos numéricos usados por la lógica (AFIP/convención interna)
      const map = {
        'IVA Responsable Inscripto': 1,
        'IVA Responsable No Inscripto': 2,
        'IVA Exento': 4,
        'Consumidor Final': 5,
        'Responsable Monotributo': 6,
        'Sujeto No Categorizado': 0
      };
      const codigo = map[tipo] ?? 1;
      setDatosForm(prev => ({ ...prev, tipoResponsable: tipo, condicionIVA: codigo }));
      return;
    }
    if (name === 'cuit') {
      // allow only digits in CUIT field
      const digits = value.replace(/\D/g, '').slice(0, 11);
      setDatosForm(prev => ({ ...prev, cuit: digits }));
      return;
    }

    if (name.startsWith('domicilio.')) {
      const field = name.split('.')[1];
      setDatosForm(prev => ({
        ...prev,
        domicilioFiscal: {
          ...prev.domicilioFiscal,
          [field]: value
        }
      }));
    } else if (name.startsWith('ingresosBrutos.')) {
      const field = name.split('.')[1];
      setDatosForm(prev => ({
        ...prev,
        ingresosBrutos: {
          ...prev.ingresosBrutos,
          [field]: value
        }
      }));
    } else {
      setDatosForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Valida CUIT (algoritmo de dígito verificador)
  const validateCUIT = (cuit) => {
    // NOTE: removed strict digit-verifier check to allow manual continuation.
    // Keep a simple format check (11 digits) but skip the algorithmic DV.
    return /^[0-9]{11}$/.test(cuit);
  };

  // No automatic AFIP lookup on blur. Manual lookup only via button.
  const handleCuitBlur = () => {
    setPadronError(null);
  };

  // Manual padron fetch removed; AFIP interaction disabled from the UI.

  // Auto-consulta removed: lookups are manual only to avoid unwanted AFIP calls.

  const handleSubmitDatos = async (e) => {
    e.preventDefault();
    setPadronError(null);
    // Basic CUIT validation before submitting
    if (!validateCUIT(String(datosForm.cuit || ''))) {
      setPadronError('CUIT inválido. Debe tener 11 dígitos.');
      return;
    }
    try {
      await dispatch(saveFiscalConfig(datosForm));
      // Re-bloquear desde el cliente como respaldo antes de refrescar el estado
      try { await dispatch(getFiscalStatus()); } catch (_) { }
    } catch (err) {
      // dejar que el slice maneje toasts/errores; solo loguear
      console.error('Error guardando datos fiscales', err);
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setCredencialesForm(prev => ({
      ...prev,
      [name]: files[0]
    }));
  };

  const handleSubmitCredenciales = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('certificado', credencialesForm.certificado);
    formData.append('clavePrivada', credencialesForm.clavePrivada);
    formData.append('passphrase', credencialesForm.passphrase);
    formData.append('ambiente', credencialesForm.ambiente);

    dispatch(uploadAfipCredentials(formData)).then(async () => {
      // Intentar re-bloquear AFIP desde el cliente como respaldo (esperar para mejorar chances de reflejar estado)
      try { await dispatch(getFiscalStatus()); } catch (_) { }
    });
  };

  // Cambiar solo el ambiente AFIP sin re-subir credenciales
  const handleAmbienteChange = async (value) => {
    try {
      setCredencialesForm(prev => ({ ...prev, ambiente: value }));
      // Llamada al backend para persistir el cambio
      const resp = await fiscalService.updateAfipAmbiente({ ambiente: value });
      toast.success(resp && resp.message ? resp.message : 'Ambiente AFIP actualizado');
      // refrescar AMBOS estados para asegurar sincronización
      try {
        await dispatch(getFiscalStatus());
        await dispatch(getFiscalConfig());
      } catch (_) { }
    } catch (err) {
      console.error('Error actualizando ambiente AFIP', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error actualizando ambiente');
    }
  };

  const handleSubmitPuntoVenta = (e) => {
    e.preventDefault();

    // Validaciones Frontend de Exclusividad y Unicidad
    const pvs = fiscalConfig?.puntosVenta || [];
    const usage = puntoVentaForm.usage || 'general';
    const numero = Number(puntoVentaForm.numero);

    // 1. Validar duplicidad de número
    const numDuplicado = pvs.find(pv => Number(pv.numero) === numero && (!editingPunto || Number(pv.numero) !== Number(editingOriginalNumero)));
    if (numDuplicado) {
      toast.error(`Ya existe un punto de venta con el número ${numero}.`);
      return;
    }

    // 2. Validar exclusividad de uso (Sistema / Ecommerce)
    if (usage === 'sistema' || usage === 'ecommerce') {
      const usoDuplicado = pvs.find(pv => pv.usage === usage && pv.isActive && (!editingPunto || Number(pv.numero) !== Number(editingOriginalNumero)));
      if (usoDuplicado) {
        toast.error(`Ya existe un punto de venta activo para "${usage}". Solo se permite uno de este tipo.`);
        return;
      }
    }

    if (editingPunto) {
      dispatch(updatePuntoVenta({ originalNumero: editingOriginalNumero, ...puntoVentaForm })).then(async () => {
        setPuntoVentaForm({ numero: '', nombre: '', tipo: 'Electrónica', usage: 'sistema' });
        setShowPuntoVentaModal(false);
        setEditingPunto(false);
        setEditingOriginalNumero(null);
        try { await dispatch(getFiscalConfig()); } catch (_) { }
        try { await dispatch(getFiscalStatus()); } catch (_) { }
      });
    } else {
      dispatch(addPuntoVenta(puntoVentaForm)).then(async () => {
        setPuntoVentaForm({ numero: '', nombre: '', tipo: 'Electrónica', usage: 'sistema' });
        setShowPuntoVentaModal(false);
        try { await dispatch(getFiscalConfig()); } catch (_) { }
        try { await dispatch(getFiscalStatus()); } catch (_) { }
      });
    }
  };

  if (loading && !fiscalConfig) {
    return <Loading />;
  }

  // Locks de edición (provienen del backend en /fiscal/status)
  // Nota: por defecto mantenemos la UI editable en el cliente. El backend debe ser la fuente de verdad
  // y rechazará operaciones si corresponde. Aquí forzamos los flags a false para dejar todo editable.
  // Conservamos `locks` por si se quiere mostrar estado, pero no lo usamos para bloquear inputs.
  // Se renombraron a variables prefijadas con _ para evitar warnings de "assigned but never used".
  const _locks = fiscalStatus?.locks || {};
  const _isDatosLocked = false; // NOTE: permitir edición por defecto
  const _isAfipLocked = false;
  const _isPVLocked = false;
  // Detectar si ya hay datos completos (CUIT, credenciales AFIP y al menos un PV)
  const hasFiscalData = !!(fiscalConfig && fiscalConfig.cuit);
  // fiscalStatus may include afip metadata
  const hasAfipCreds = !!(fiscalStatus && ((fiscalStatus.hasCredentials === true) || fiscalStatus.hasCertStored === true || fiscalStatus.hasKeyStored === true || fiscalStatus.certificadoVencimiento));
  const hasAnyPV = !!(fiscalConfig && Array.isArray(fiscalConfig.puntosVenta) && fiscalConfig.puntosVenta.length > 0);
  // If all three are present, prefer to disable the UI even if locks haven't been set server-side yet
  const _shouldForceDisableByData = hasFiscalData && hasAfipCreds && hasAnyPV;
  // Desactivar cualquier heurística que bloquee la UI en el cliente
  const _forceDisableEditing = false;
  const _forceDisableAfipByData = false;
  const _forceDisablePVByData = false;



  // Estilos
  const containerStyle = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '30px'
  };

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '10px'
  };

  const subtitleStyle = {
    fontSize: '14px',
    color: '#718096'
  };

  const tabsContainerStyle = {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    borderBottom: '2px solid #e2e8f0',
    paddingBottom: '0'
  };

  const tabStyle = (isActive) => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    background: 'transparent',
    color: isActive ? '#667eea' : '#718096',
    borderBottom: isActive ? '3px solid #667eea' : '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginBottom: '-2px'
  });

  const cardStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    marginBottom: '20px'
  };

  const sectionTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #e2e8f0'
  };

  const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '20px'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s'
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const buttonStyle = (variant = 'primary') => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: variant === 'primary'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : variant === 'success'
        ? '#48bb78'
        : variant === 'secondary'
          ? '#718096'
          : '#e2e8f0',
    color: variant === 'primary' || variant === 'success' || variant === 'secondary' ? '#ffffff' : '#2d3748'
  });

  const statusBadgeStyle = (status) => ({
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    background: status ? '#c6f6d5' : '#fed7d7',
    color: status ? '#22543d' : '#742a2a'
  });

  const fileInputStyle = {
    width: '100%',
    padding: '10px',
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#4a5568'
  };

  const alertStyle = (type = 'info') => ({
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '20px',
    background: type === 'warning' ? '#fef5e7' : type === 'success' ? '#e8f5e9' : '#e3f2fd',
    border: `1px solid ${type === 'warning' ? '#f39c12' : type === 'success' ? '#4caf50' : '#2196f3'}`,
    color: type === 'warning' ? '#856404' : type === 'success' ? '#1b5e20' : '#0d47a1',
    fontSize: '14px'
  });

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const modalContentStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '20px'
  };

  const thStyle = {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#2d3748'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#4a5568'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h1 style={titleStyle}>🧾 Configuración Fiscal y AFIP</h1>
          <button
            onClick={() => navigate('/dashboard')}
            style={buttonStyle('secondary')}
          >
            ← Volver al Dashboard
          </button>
        </div>
        <p style={subtitleStyle}>
          Configure sus datos fiscales y credenciales para facturación electrónica
        </p>
      </div>

      {/* Estado de Configuración (se muestra debajo en la tarjeta cuando `fiscalStatus` existe) */}

      {fiscalStatus && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <strong>Estado Configuración:</strong>{' '}
              <span style={statusBadgeStyle(fiscalStatus.isConfigured)}>
                {fiscalStatus.isConfigured ? '✅ Configurado' : '❌ Pendiente'}
              </span>
            </div>
            <div>
              <strong>Credenciales AFIP:</strong>{' '}
              {
                (() => {
                  // Defensive handling: backend may return hasCertStored/hasKeyStored (new)
                  // or only hasCredentials (older). Compute a clear UI state.
                  const hs = fiscalStatus || {};
                  let hasCert = typeof hs.hasCertStored !== 'undefined' ? hs.hasCertStored : undefined;
                  let hasKey = typeof hs.hasKeyStored !== 'undefined' ? hs.hasKeyStored : undefined;
                  const hasCreds = typeof hs.hasCredentials !== 'undefined' ? hs.hasCredentials : undefined;

                  // Backward compatibility: if server only returns hasCredentials=true,
                  // assume both are present for display purposes.
                  if (hasCreds === true && (typeof hasCert === 'undefined' || typeof hasKey === 'undefined')) {
                    hasCert = typeof hasCert === 'undefined' ? true : hasCert;
                    hasKey = typeof hasKey === 'undefined' ? true : hasKey;
                  }

                  const both = !!(hasCert && hasKey);
                  const one = !!((hasCert && !hasKey) || (!hasCert && hasKey));

                  if (both) {
                    return (
                      <>
                        <span style={statusBadgeStyle(true)}>✅ Cargadas</span>
                        {fiscalStatus?.certificadoVencimiento && (
                          <span style={{ marginLeft: 12 }}>
                            <span style={{ ...statusBadgeStyle(true), display: 'inline-block' }}>
                              📅 Vencimiento: {new Date(fiscalStatus.certificadoVencimiento).toLocaleDateString('es-AR')}
                            </span>
                          </span>
                        )}
                      </>
                    );
                  }
                  if (one) {
                    const missing = !hasCert ? 'certificado' : 'clave privada';
                    return (
                      <span style={{ ...statusBadgeStyle(false), background: '#fff3cd', color: '#856404' }} title={`Falta ${missing}`}>
                        ⚠️ Parciales
                      </span>
                    );
                  }

                  // Default: show Faltantes
                  return <span style={statusBadgeStyle(false)}>❌ Faltantes</span>;
                })()
              }
            </div>
            {/* Ambiente display intentionally removed — ambiente is inferred from uploaded credentials. */}
          </div>

          {/* Botón para listar/descargar archivos fiscales */}
          <div style={{ marginTop: 12 }}>
            <button
              onClick={async () => {
                setFilesError(null);
                setFilesLoading(true);
                try {
                  const blob = await fiscalService.getFiscalFilesZip();
                  // Crear URL y forzar descarga
                  const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'configuracion.zip';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('Error descargando ZIP de archivos fiscales', err);
                  setFilesError(err.response?.data?.message || err.message || 'Error al descargar archivos');
                } finally {
                  setFilesLoading(false);
                }
              }}
              style={buttonStyle('primary')}
            >
              {filesLoading ? '⏳ Preparando descarga...' : '⬇️ Descargar configuración (ZIP)'}
            </button>

            {filesError && <div style={{ marginTop: 8, color: '#b91c1c' }}>{filesError}</div>}
          </div>

          {fiscalStatus.certificateExpiringSoon && (
            <div style={{ ...alertStyle('warning'), marginTop: '15px' }}>
              ⚠️ Su certificado AFIP está próximo a vencer. Renuévelo pronto.
            </div>
          )}

          {/* Diagnostic button removed — diagnostics endpoint kept for debugging but not exposed in UI. */}
        </div>
      )}

      {/* Tabs */}
      <div style={tabsContainerStyle}>
        <button
          style={tabStyle(activeTab === 'datos-fiscales')}
          onClick={() => setActiveTab('datos-fiscales')}
        >
          📋 Datos Fiscales
        </button>
        <button
          style={tabStyle(activeTab === 'credenciales')}
          onClick={() => setActiveTab('credenciales')}
        >
          🔐 Credenciales AFIP
        </button>
        <button
          style={tabStyle(activeTab === 'puntos-venta')}
          onClick={() => setActiveTab('puntos-venta')}
        >
          🏪 Puntos de Venta
        </button>
      </div>

      {/* Tab: Datos Fiscales */}
      {activeTab === 'datos-fiscales' && (
        <form onSubmit={handleSubmitDatos}>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <div style={cardStyle}>
              <h3 style={sectionTitleStyle}>Información del Contribuyente</h3>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    CUIT <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="cuit"
                    value={datosForm.cuit}
                    onChange={handleDatosChange}
                    onBlur={handleCuitBlur}
                    placeholder="20-12345678-9"
                    style={inputStyle}
                    maxLength="11"
                    required
                  />
                  {padronLoading && <div style={{ fontSize: 12, color: '#4a5568', marginTop: 6 }}>⏳ Consultando AFIP...</div>}
                  {padronError && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{padronError}</div>}
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Razón Social <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="razonSocial"
                    value={datosForm.razonSocial}
                    onChange={handleDatosChange}
                    placeholder="Mi Empresa S.A."
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>IIBB (Ingresos Brutos)</label>
                  <input
                    type="text"
                    name="ingresosBrutos.numero"
                    value={datosForm.ingresosBrutos?.numero || ''}
                    onChange={handleDatosChange}
                    placeholder="123456789"
                    style={inputStyle}
                  />
                </div>

                {/* Nota: no mostramos la condición de IIBB en la pantalla, solo el número (requerimiento del usuario) */}
              </div>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Nombre de Fantasía</label>
                  <input
                    type="text"
                    name="nombreFantasia"
                    value={datosForm.nombreFantasia}
                    onChange={handleDatosChange}
                    placeholder="Mi Tienda"
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Tipo de Responsable <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="tipoResponsable"
                    value={datosForm.tipoResponsable}
                    onChange={handleDatosChange}
                    style={selectStyle}
                    required
                  >
                    <option value="IVA Responsable Inscripto">IVA Responsable Inscripto</option>
                    <option value="IVA Exento">IVA Exento</option>
                    <option value="Responsable Monotributo">Responsable Monotributo</option>
                  </select>
                </div>
              </div>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Inicio de Actividades <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="inicioActividades"
                    value={datosForm.inicioActividades}
                    onChange={handleDatosChange}
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Email <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={datosForm.email}
                    onChange={handleDatosChange}
                    placeholder="facturacion@empresa.com"
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  Teléfono <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={datosForm.telefono}
                  onChange={handleDatosChange}
                  placeholder="+54 11 1234-5678"
                  style={{ ...inputStyle, maxWidth: '400px' }}
                  required
                />
              </div>
            </div>

            <div style={cardStyle}>
              <h3 style={sectionTitleStyle}>Domicilio Fiscal</h3>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Calle <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="domicilio.calle"
                    value={datosForm.domicilioFiscal.calle}
                    onChange={handleDatosChange}
                    placeholder="Av. Corrientes"
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Número <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="domicilio.numero"
                    value={datosForm.domicilioFiscal.numero}
                    onChange={handleDatosChange}
                    placeholder="1234"
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Piso</label>
                  <input
                    type="text"
                    name="domicilio.piso"
                    value={datosForm.domicilioFiscal.piso}
                    onChange={handleDatosChange}
                    placeholder="5"
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Departamento</label>
                  <input
                    type="text"
                    name="domicilio.departamento"
                    value={datosForm.domicilioFiscal.departamento}
                    onChange={handleDatosChange}
                    placeholder="B"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={formGridStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Localidad <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="domicilio.localidad"
                    value={datosForm.domicilioFiscal.localidad}
                    onChange={handleDatosChange}
                    placeholder="Buenos Aires"
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Provincia <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="domicilio.provincia"
                    value={datosForm.domicilioFiscal.provincia}
                    onChange={handleDatosChange}
                    placeholder="CABA"
                    style={inputStyle}
                    required
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  Código Postal <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="domicilio.codigoPostal"
                  value={datosForm.domicilioFiscal.codigoPostal}
                  onChange={handleDatosChange}
                  placeholder="1043"
                  style={{ ...inputStyle, maxWidth: '200px' }}
                  required
                />
              </div>
            </div>

            <button type="submit" style={buttonStyle('primary')} disabled={loading}>
              {loading ? '⏳ Guardando...' : '💾 Guardar Configuración'}
            </button>
          </fieldset>
          {/* Desactivar comportamiento de bloqueo en UI: la edición siempre está disponible desde cliente. */}
        </form>
      )}

      {/* Tab: Credenciales AFIP */}
      {activeTab === 'credenciales' && (
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Certificado y Clave Privada AFIP</h3>

          {!fiscalConfig?.isConfigured && (
            <div style={alertStyle('warning')}>
              ⚠️ Primero debe completar sus datos fiscales antes de cargar las credenciales AFIP.
            </div>
          )}

          <div style={alertStyle('info')}>
            ℹ️ <strong>Instrucciones:</strong><br />
            1. Genere su certificado en el sitio de AFIP (Administrador de Relaciones de Clave Fiscal)<br />
            2. Descargue el certificado (.crt) y la clave privada (.key)<br />
            3. Cargue ambos archivos aquí de forma segura (se encriptan automáticamente)
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmitCredenciales(e); }}>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  Certificado AFIP (.crt o .pem) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="file"
                  name="certificado"
                  onChange={handleFileChange}
                  accept=".crt,.pem"
                  style={fileInputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>
                  Clave Privada (.key o .pem) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="file"
                  name="clavePrivada"
                  onChange={handleFileChange}
                  accept=".key,.pem"
                  style={fileInputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Passphrase (si la clave está protegida)</label>
                <input
                  type="password"
                  name="passphrase"
                  value={credencialesForm.passphrase}
                  onChange={(e) => setCredencialesForm({ ...credencialesForm, passphrase: e.target.value })}
                  placeholder="Dejar en blanco si no tiene"
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Ambiente AFIP</label>
                <select
                  name="ambiente"
                  value={credencialesForm.ambiente}
                  onChange={(e) => handleAmbienteChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="testing">Testing / Homologación</option>
                  <option value="production">Producción</option>
                </select>
                <div style={{ marginTop: 8, fontSize: 13, color: '#718096' }}>Seleccioná el ambiente que se usará al comunicarse con AFIP.</div>
              </div>

              <button
                type="submit"
                style={buttonStyle('success')}
                disabled={loading || !fiscalConfig?.isConfigured}
              >
                {loading ? '⏳ Cargando...' : '🔐 Cargar Credenciales'}
              </button>
            </fieldset>
          </form>


          {fiscalStatus?.certificadoVencimiento && (
            <div style={{ marginTop: '30px', padding: '15px', background: '#f7fafc', borderRadius: '8px' }}>
              <strong>Información del Certificado:</strong><br />
              Vencimiento: {new Date(fiscalStatus.certificadoVencimiento).toLocaleDateString('es-AR')}
            </div>
          )}
        </div>
      )}

      {/* Tab: Puntos de Venta */}
      {activeTab === 'puntos-venta' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={sectionTitleStyle}>Puntos de Venta Registrados</h3>
            <button
              onClick={() => {
                setPuntoVentaForm({ numero: '', nombre: '', tipo: 'Electrónica', usage: 'sistema' });
                setEditingPunto(false);
                setShowPuntoVentaModal(true);
              }}
              style={buttonStyle('primary')}
            >
              ➕ Agregar Punto de Venta
            </button>
          </div>

          {fiscalConfig?.puntosVenta?.length > 0 ? (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Número</th>
                  <th style={thStyle}>Nombre</th>
                  <th style={thStyle}>Tipo</th>
                  <th style={thStyle}>Estado</th>
                  <th style={thStyle}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fiscalConfig.puntosVenta.map((pv, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{String(pv.numero).padStart(5, '0')}</td>
                    <td style={tdStyle}>{pv.nombre}</td>
                    <td style={tdStyle}>
                      {pv.usage === 'sistema' ? <span style={{ fontWeight: 'bold', color: '#3182ce' }}>Sistema (Local)</span> :
                        pv.usage === 'ecommerce' ? <span style={{ fontWeight: 'bold', color: '#d69e2e' }}>Ecommerce</span> :
                          pv.tipo}
                    </td>
                    <td style={tdStyle}>
                      <span style={statusBadgeStyle(pv.isActive)}>
                        {pv.isActive ? '✅ Activo' : '❌ Inactivo'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => {
                          setPuntoVentaForm({ numero: pv.numero, nombre: pv.nombre, tipo: pv.tipo, usage: pv.usage || 'general' });
                          setEditingPunto(true);
                          setEditingOriginalNumero(pv.numero);
                          setShowPuntoVentaModal(true);
                        }}
                        style={buttonStyle('secondary')}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const ok = window.confirm(`Eliminar punto de venta ${String(pv.numero).padStart(5, '0')} - ${pv.nombre}? Esta acción no se puede deshacer.`);
                            if (!ok) return;
                            await fiscalService.deletePuntoVenta(pv.numero);
                            // refrescar config y estado
                            try { await dispatch(getFiscalConfig()); } catch (_) { }
                            try { await dispatch(getFiscalStatus()); } catch (_) { }
                          } catch (err) {
                            console.error('Error eliminando punto de venta', err);
                            alert(err?.response?.data?.message || err?.message || 'Error eliminando punto de venta');
                          }
                        }}
                        style={{ ...buttonStyle('secondary'), marginLeft: 8, background: '#e53e3e' }}
                      >
                        🗑️ Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={alertStyle('info')}>
              📌 No hay puntos de venta registrados. Agregue al menos uno para poder facturar.
            </div>
          )}
        </div>
      )}

      {/* Modal: Agregar Punto de Venta */}
      {showPuntoVentaModal && (
        <div style={modalOverlayStyle} onClick={() => setShowPuntoVentaModal(false)}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
              {editingPunto ? '✏️ Editar Punto de Venta' : '➕ Agregar Punto de Venta'}
            </h3>

            <form onSubmit={(e) => { e.preventDefault(); handleSubmitPuntoVenta(e); }}>
              <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Número <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={puntoVentaForm.numero}
                    onChange={(e) => setPuntoVentaForm({ ...puntoVentaForm, numero: e.target.value })}
                    placeholder="1"
                    style={inputStyle}
                    min="1"
                    max="99999"
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Nombre <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={puntoVentaForm.nombre}
                    onChange={(e) => setPuntoVentaForm({ ...puntoVentaForm, nombre: e.target.value })}
                    placeholder="Caja Principal"
                    style={inputStyle}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>
                    Tipo de Punto de Venta <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    value={`${puntoVentaForm.tipo}|${puntoVentaForm.usage || 'general'}`}
                    onChange={(e) => {
                      const [tipo, usage] = e.target.value.split('|');
                      // Auto-suggest name if empty
                      let nombre = puntoVentaForm.nombre;
                      if (!nombre) {
                        if (usage === 'sistema') nombre = 'Caja Local';
                        if (usage === 'ecommerce') nombre = 'Ventas Web';
                      }
                      setPuntoVentaForm({ ...puntoVentaForm, tipo, usage, nombre });
                    }}
                    style={selectStyle}
                  >
                    <option value="Electrónica|sistema">Sistema (Local) - WebService</option>
                    <option value="Electrónica|ecommerce">Ecommerce - Exclusivo WebService</option>
                  </select>
                  {puntoVentaForm.usage === 'ecommerce' && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#856404', backgroundColor: '#fff3cd', padding: '8px', borderRadius: '4px' }}>
                      ⚠️ <strong>Atención:</strong> Este punto de venta será de uso exclusivo para facturación automática desde plataformas de Ecommerce. No debe usarse para ventas locales.
                    </div>
                  )}
                  {puntoVentaForm.usage === 'sistema' && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#004085', backgroundColor: '#cce5ff', padding: '8px', borderRadius: '4px' }}>
                      ℹ️ Este será el punto de venta principal para las ventas realizadas en el local.
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" style={buttonStyle('primary')}>
                    {editingPunto ? '💾 Actualizar' : '💾 Guardar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPuntoVentaModal(false); setEditingPunto(false); setEditingOriginalNumero(null); }}
                    style={buttonStyle('secondary')}
                  >
                    ❌ Cancelar
                  </button>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      )}
      {/* Edición de Puntos de Venta habilitada en la UI cliente */}
    </div>
  );
};

export default FiscalConfig;
